import TopNavSimple from '#app/components/top-nav-simple.tsx';

function TermsOfService() {
  return (
    <>
      <TopNavSimple />

      <div className="max-w-4xl mx-auto p-6 text-white/80 mt-20">
        <h1 className="text-3xl font-bold mb-10 text-white">
          Terms of Service
        </h1>
        <p className="text-sm text-white/60 mb-6">
          <strong>Last Updated:</strong> September 10, 2025
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Application Overview</h2>
          <p className="mb-4">
            CircuitBread Insights is a comprehensive internal analytics and sponsor relationship management platform designed for the electronics education community. The platform integrates multiple data sources to provide unified analytics, generate professional sponsor reports, and manage business relationships across digital content platforms.
          </p>
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mt-4">
            <p className="font-medium text-blue-200 mb-2">📋 Official CircuitBread Accounts Only</p>
            <p className="text-sm text-blue-100">
              <strong>Important:</strong> CircuitBread Insights exclusively accesses and analyzes data from CircuitBread's own official, verified accounts across all integrated platforms. We do not access, monitor, or analyze any third-party accounts, individual users, or external organizations.
            </p>
          </div>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Platform Capabilities (CircuitBread Official Accounts)</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li><strong>CircuitBread's Multi-platform Analytics:</strong> YouTube channel, Facebook page, Instagram account, TikTok account, LinkedIn company page</li>
            <li><strong>CircuitBread Website:</strong> Article performance tracking and engagement metrics from CircuitBread.com</li>
            <li><strong>CircuitBread Newsletter:</strong> Email campaign analytics through official Mailchimp account</li>
            <li><strong>Sponsor Reporting:</strong> Automated PDF report generation showing CircuitBread's content performance</li>
            <li><strong>Business Management:</strong> Friend/sponsor relationship tracking and contact management</li>
            <li><strong>Historical Analysis:</strong> Quarterly and all-time reporting for CircuitBread's owned content</li>
            <li><strong>Secure Access:</strong> User authentication with multi-factor support for internal team</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Access and Eligibility</h2>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Authorized Use Only</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Platform access restricted to authorized CircuitBread personnel and designated partners</li>
            <li>Users must have valid business justification for platform access</li>
            <li>Access privileges granted based on role and responsibilities</li>
            <li>All access subject to background verification and approval process</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Authentication Requirements</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Valid email address and secure password meeting complexity requirements</li>
            <li>Multi-factor authentication strongly recommended for enhanced security</li>
            <li>WebAuthn/Passkey support available for biometric authentication</li>
            <li>Session management with automatic timeout for inactive accounts</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Account Responsibilities</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Users responsible for maintaining confidentiality of login credentials</li>
            <li>Immediate notification required for suspected account compromise</li>
            <li>Regular password updates recommended for security</li>
            <li>Shared accounts or credential sharing strictly prohibited</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Acceptable Use and Data Handling</h2>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Permitted Uses (CircuitBread Content Only)</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Generate comprehensive analytics reports for <strong>CircuitBread's content performance</strong> across official channels</li>
            <li>Create professional sponsor presentations demonstrating <strong>CircuitBread's content ROI</strong> to business partners</li>
            <li>Manage friend/sponsor relationships and track <strong>CircuitBread's sponsored content</strong> associations</li>
            <li>Analyze <strong>CircuitBread's cross-platform campaign effectiveness</strong> for strategic planning</li>
            <li>Monitor <strong>CircuitBread's content performance trends</strong> across quarterly and historical periods</li>
            <li>Export <strong>CircuitBread's analytics data</strong> in approved formats for business reporting</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Data Handling Requirements</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li><strong>CircuitBread-owned data only:</strong> Analytics used exclusively for CircuitBread's legitimate business purposes</li>
            <li><strong>No external redistribution:</strong> CircuitBread's performance data not shared outside organization</li>
            <li><strong>API compliance:</strong> Full compliance with third-party terms (YouTube, Facebook, Instagram, TikTok, LinkedIn, Mailchimp)</li>
            <li><strong>Sponsor reporting scope:</strong> Reports limited to CircuitBread's aggregated content performance metrics only</li>
            <li><strong>Privacy protection:</strong> No access to individual follower data or personal information from any platform</li>
          </ul>
          <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 mt-4">
            <p className="font-medium text-green-200 mb-2">🔒 Data Access Clarification</p>
            <p className="text-sm text-green-100">
              <strong>CircuitBread Insights only accesses metrics and analytics from CircuitBread's own official accounts.</strong> We do not have access to any other companies' accounts, individual creator accounts, or third-party analytics data.
            </p>
          </div>

          <h3 className="text-lg font-medium mb-3 mt-6">Platform Integrity</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Users must not attempt to reverse engineer or extract proprietary algorithms</li>
            <li>No unauthorized access to data beyond assigned permission levels</li>
            <li>Respect rate limits and API quotas for third-party integrations</li>
            <li>Report security vulnerabilities or data anomalies to administrators</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Prohibited Activities</h2>
          <p className="mb-4">The following activities are strictly prohibited when using CircuitBread Insights:</p>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Security Violations</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Attempting to access accounts, data, or features without proper authorization</li>
            <li>Sharing login credentials or enabling unauthorized access to the platform</li>
            <li>Circumventing security measures, rate limits, or access controls</li>
            <li>Using automated tools or scripts without explicit administrator approval</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Data Misuse</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Downloading or extracting <strong>CircuitBread's analytics data</strong> for unauthorized purposes</li>
            <li>Sharing <strong>CircuitBread's performance data</strong> with competitors or unauthorized third parties</li>
            <li>Using <strong>CircuitBread's platform data</strong> for purposes unrelated to CircuitBread business</li>
            <li>Attempting to reverse-engineer or misuse <strong>CircuitBread's analytics insights</strong></li>
          </ul>
          <p className="text-sm text-white/70 mt-3 pl-4">
            <strong>Note:</strong> All prohibited activities relate to CircuitBread's own data - we do not have access to any external accounts or third-party data.
          </p>

          <h3 className="text-lg font-medium mb-3 mt-6">Platform Abuse</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Overloading the platform with excessive requests or API calls</li>
            <li>Attempting to disrupt platform operations or availability</li>
            <li>Using the platform for any illegal or unauthorized commercial activities</li>
            <li>Violating any applicable laws, regulations, or third-party terms of service</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Service Availability and Support</h2>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Platform Availability</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>CircuitBread Insights provided on an "as-is" and "as-available" basis</li>
            <li>Scheduled maintenance and updates may temporarily affect availability</li>
            <li>Third-party API limitations may impact data refresh capabilities</li>
            <li>Platform performance subject to infrastructure and network conditions</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Technical Support</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Internal support provided for authorized users during business hours</li>
            <li>Bug reports and feature requests handled through established channels</li>
            <li>Training and documentation available for platform capabilities</li>
            <li>Emergency support available for critical business impact issues</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Intellectual Property and Confidentiality</h2>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Platform Ownership</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>CircuitBread Insights platform and its source code remain property of CircuitBread</li>
            <li>Users granted limited, non-exclusive access for authorized business purposes</li>
            <li>No rights granted to modify, distribute, or create derivative works</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">Data Confidentiality</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>All analytics data and business information considered confidential</li>
            <li>Users must maintain confidentiality during and after access period</li>
            <li>Sponsor relationships and performance data protected as trade secrets</li>
            <li>Non-disclosure obligations survive termination of platform access</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Limitation of Liability</h2>
          <p className="mb-4">
            CircuitBread provides the Insights platform for internal business use. To the maximum extent permitted by law:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Platform provided without warranties of any kind, express or implied</li>
            <li>No guarantee of data accuracy, completeness, or third-party API availability</li>
            <li>Users responsible for verifying data accuracy for business decisions</li>
            <li>CircuitBread not liable for business decisions based on platform analytics</li>
            <li>Liability limited to direct damages and capped at reasonable business limits</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Account Termination and Suspension</h2>
          
          <h3 className="text-lg font-medium mb-3 mt-6">Termination by CircuitBread</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Immediate termination for violation of these terms or security policies</li>
            <li>Suspension pending investigation of suspected unauthorized activities</li>
            <li>Account deactivation upon employment termination or role change</li>
            <li>Temporary suspension for maintenance or security reasons</li>
          </ul>

          <h3 className="text-lg font-medium mb-3 mt-6">User-Initiated Termination</h3>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Users may request account deactivation through administrators</li>
            <li>Data access terminated upon account closure</li>
            <li>Confidentiality obligations survive account termination</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Updates and Modifications</h2>
          <p className="mb-4">
            CircuitBread reserves the right to modify these Terms of Service to reflect:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4 mb-4">
            <li>Changes in platform capabilities or features</li>
            <li>Updates to legal requirements or compliance standards</li>
            <li>Modifications to third-party API terms or data policies</li>
            <li>Improvements to security or data handling procedures</li>
          </ul>
          <p className="mb-4">
            Users will be notified of material changes through platform notifications or email. Continued use constitutes acceptance of updated terms.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Governing Law and Compliance</h2>
          <p className="mb-4">
            These Terms of Service are governed by applicable laws and regulations. Users agree to:
          </p>
          <ul className="list-disc list-outside ml-4 space-y-2 [&>li]:leading-normal pl-4">
            <li>Comply with all applicable local, state, and federal laws</li>
            <li>Respect third-party intellectual property and API terms</li>
            <li>Follow industry best practices for data handling and security</li>
            <li>Report violations or suspected unauthorized activities</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Contact Information</h2>
          <p className="mb-4">
            For questions about these terms, technical support, or access requests:
          </p>
          <ul className="list-none space-y-2">
            <li>
              <strong>General Inquiries:</strong>{' '}
              <a
                href="mailto:info@insights.circuitbread.com"
                className="underline-offset-4 hover:no-underline"
              >
                info@insights.circuitbread.com
              </a>
            </li>
            <li><strong>Platform:</strong> CircuitBread Insights Analytics</li>
            <li><strong>Organization:</strong> CircuitBread Electronics Education</li>
            <li><strong>Support Hours:</strong> Business hours (Mountain Time)</li>
          </ul>
          
          <p className="mt-6 text-sm text-white/60">
            By accessing and using CircuitBread Insights, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.
          </p>
        </section>
      </div>
    </>
  );
}

export default TermsOfService;
